import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { ThemeSwitcher } from './ThemeSwitcher';
import { useSignalRHub } from '@/realtime/useSignalRHub';
import { LoginSearch } from '@/features/tenant/loginOverview/LoginSearch';
import { LoginOverviewModal } from '@/features/tenant/loginOverview/LoginOverviewModal';
import { NotificationBell } from '@/features/common/notifications/NotificationBell';

interface ConnectorStatusItem {
  name: string;
  isConnected: boolean;
}

function HeaderDropdown({
  toggle,
  children,
  className = '',
  menuClassName = '',
  toggleClassName = '',
}: {
  toggle: ReactNode;
  children: ReactNode;
  className?: string;
  menuClassName?: string;
  toggleClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className={`header-element dropdown ${className}`} ref={ref} style={{ position: 'relative' }}>
      <a
        href="#"
        className={`header-link dropdown-toggle ${toggleClassName}`.trim()}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        {toggle}
      </a>
      <div
        className={`dropdown-menu dropdown-menu-end ${menuClassName} ${open ? 'show' : ''}`}
        style={{ position: 'absolute', right: 0, left: 'auto', top: '100%', bottom: 'auto', transform: 'none', marginTop: '0.25rem' }}
        onClick={() => setOpen(false)}
      >
        {children}
      </div>
    </div>
  );
}

// Live connector (data-source) status via the ConnectorStatus SignalR hub.
// Payload: { name, isConnected }[] on "OnConnectorStatusReceived".
function ConnectorStatus() {
  const [items, setItems] = useState<ConnectorStatusItem[]>([]);

  useSignalRHub({
    url: '/hubs/connector-status',
    on: {
      OnConnectorStatusReceived: (...args: unknown[]) => {
        const data = (args[0] as ConnectorStatusItem[] | null) ?? [];
        setItems(data);
      },
    },
  });

  if (items.length === 0) return null;

  // Disconnected first (worst surfaced), matching the legacy connector-status.js.
  const sorted = [...items].sort((a, b) => (a.isConnected === b.isConnected ? 0 : a.isConnected ? 1 : -1));

  const badge = (item: ConnectorStatusItem, margin = '') => (
    <div className={`dropdown nav-link connection-status${margin ? ` ${margin}` : ''}`}>
      <span className="badge badge-sm bg-dark">{item.name}</span>
      <span className={item.isConnected ? 'pulse' : 'pulse-danger'} />
    </div>
  );

  // ≤ 2 connectors: all shown inline, no dropdown.
  if (sorted.length <= 2) {
    return (
      <div id="connector-status-inline" className="header-element d-flex align-items-center mx-2">
        {sorted.map((c, i) => (
          <span key={c.name}>{badge(c, i === 0 && sorted.length > 1 ? 'mx-3' : '')}</span>
        ))}
      </div>
    );
  }

  // > 2: first badge inline + a caret dropdown with the full grid.
  return (
    <>
      <div id="connector-status-inline" className="header-element d-flex align-items-center mx-2">
        {badge(sorted[0], 'mx-1')}
      </div>
      <HeaderDropdown
        className="source-container connectors-dropdown"
        menuClassName="pt-0 overflow-hidden"
        toggle={<i className="fa-solid fa-caret-down header-link-icon fs-18" />}
      >
        <div style={{ minWidth: 280 }}>
          <div className="p-3 border-bottom">
            <h6 className="mb-0">Connectors</h6>
          </div>
          <div className="px-2 pb-3 pt-2">
            <div className="connection-status-dropdown d-flex flex-wrap gap-2">
              {sorted.map((c) => (
                <div key={c.name} className="connector-grid-item">
                  {badge(c)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </HeaderDropdown>
    </>
  );
}

const CLOCK_FMT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZoneName: 'shortOffset',
};

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  // e.g. "Sat, Oct 10, 2026, 19:40:00 GMT+3"
  return (
    <div className="header-element d-none d-lg-flex align-items-center text-muted font-monospace">
      <i className="ri-time-line me-1" />
      <span>{now.toLocaleString('en-US', CLOCK_FMT)}</span>
    </div>
  );
}

function FullScreenToggle() {
  function toggle() {
    if (!document.fullscreenElement) void document.documentElement.requestFullscreen().catch(() => {});
    else void document.exitFullscreen().catch(() => {});
  }
  return (
    <div className="header-element d-none d-lg-flex align-items-center">
      <a
        href="#"
        className="header-link"
        onClick={(e) => {
          e.preventDefault();
          toggle();
        }}
        aria-label="Full screen"
      >
        <i className="ri-fullscreen-line header-link-icon fs-18" />
      </a>
    </div>
  );
}

export function Header({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const { user, tenantName, logout, grantedPolicies, isImpersonating, returnToSelf } = useAuth();
  const navigate = useNavigate();
  // Host (no tenant) has no logins to search and no per-tenant server time — the
  // login-search box and the clock are tenant-side only (matches legacy host header).
  const isHost = !!user?.isAuthenticated && !user.tenantId;
  const canSeeNotifications = grantedPolicies['User.Notifications'] === true;
  const displayName = user?.name || user?.userName || 'User';
  const email = user?.email || '';
  const [loModal, setLoModal] = useState<{ login: number; connector: number } | null>(null);

  return (
    <header className="app-header" id="main-navbar">
      <div className="main-header-container container-fluid">
        <div className="header-content-left">
          <div className="header-element">
            {/* noa draws the hamburger via .sidemenu-toggle::before — no inner icon. */}
            <a
              aria-label="Toggle sidebar"
              className="sidemenu-toggle header-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onToggleSidebar();
              }}
            />
          </div>

          <div className="header-element d-none d-md-flex align-items-center">
            <Link to="/User/FAQ" className="header-link" aria-label="FAQ">
              {/* Match the legacy header: FontAwesome outlined question-circle (not Remix). */}
              <i className="fa-regular fa-circle-question header-link-icon fs-18 text-muted" />
            </Link>
          </div>

          {!isHost && (
            <div className="header-element header-search d-none d-md-flex align-items-center ms-2" style={{ minWidth: 240 }}>
              <LoginSearch className="w-100" onSelect={(login, connector) => setLoModal({ login, connector })} />
            </div>
          )}
        </div>

        <div className="header-content-right d-flex align-items-center">
          {!isHost && <Clock />}

          <ConnectorStatus />

          {/* Notifications */}
          {canSeeNotifications && (
            <NotificationBell onOpenLogin={(login, connector) => setLoModal({ login, connector })} />
          )}

          <FullScreenToggle />
          <ThemeSwitcher />

          {/* Profile — username + caret, matching the legacy header ("admin ⌄").
              (Full name/email/tenant live in the dropdown.) */}
          <HeaderDropdown
            toggleClassName="pt-4"
            toggle={
              <span className="d-flex align-items-center gap-1">
                <span className="fw-medium">{displayName}</span>
                <i className="ri-arrow-down-s-line fs-16 text-muted" />
              </span>
            }
          >
            <div className="dropdown-header">
              <div className="fw-semibold">{displayName}</div>
              {email && <div className="text-muted small">{email}</div>}
              <div className="text-muted small">{tenantName || 'Host'}</div>
            </div>
            <div className="dropdown-divider" />
            {isImpersonating && (
              <button
                className="dropdown-item text-primary fw-medium"
                type="button"
                onClick={() => {
                  void returnToSelf().then(() => navigate('/'));
                }}
              >
                <i className="ri-arrow-go-back-line me-2" />
                Return to my account
              </button>
            )}
            <Link className="dropdown-item" to="/profile">
              <i className="ri-user-line me-2" />
              My Profile
            </Link>
            <button className="dropdown-item" type="button" onClick={() => void logout()}>
              <i className="ri-logout-box-r-line me-2" />
              Sign out
            </button>
          </HeaderDropdown>
        </div>
      </div>

      {loModal && (
        <LoginOverviewModal login={loModal.login} connector={loModal.connector} onClose={() => setLoModal(null)} />
      )}
    </header>
  );
}
